import * as React from 'react';
import { useState, useEffect } from 'react';
import PhoneInput, { isValidPhoneNumber, Country } from 'react-phone-number-input';
import { getExampleNumber, AsYouType, getCountryCallingCode } from 'libphonenumber-js/max';
import examples from 'libphonenumber-js/examples.mobile.json';
import 'react-phone-number-input/style.css';
import './IntlPhoneInput.css';

interface IntlPhoneInputProps {
  id?: string;
  value: string | undefined; // Explicitly handle undefined as well
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  defaultCountry?: string;
  preferredCountries?: string[];
  onBlur?: () => void;
  style?: React.CSSProperties;
}

export const IntlPhoneInput: React.FC<IntlPhoneInputProps> = ({
  id,
  value,
  onChange,
  required = false,
  disabled = false,
  defaultCountry = 'US',
  preferredCountries = [],
  onBlur,
  style,
}) => {
  const [touched, setTouched] = useState(false);
  const [country, setCountry] = useState<Country>(defaultCountry as Country);
  const [exampleNumber, setExampleNumber] = useState<string>('');

  useEffect(() => {
    try {
      const example = getExampleNumber(country, examples);
      if (example) {
        const formatted = new AsYouType(country).input(example.nationalNumber);
        setExampleNumber(formatted);
      } else {
        setExampleNumber('');
      }
    } catch (err) {
      console.error('Error creating example number:', err);
      setExampleNumber('');
    }
  }, [country]);

  const handlePhoneNumberChange = (val: string | undefined) => {
    onChange(val || '');
  };

  const handleBlur = () => {
    setTouched(true);
    onBlur?.();
  };

  const invalidClass =
    (value && !isValidPhoneNumber(value)) || (!value && touched && required) ? 'is-invalid' : '';

  const showPlaceholder = !value || !exampleNumber || exampleNumber.length === 0;

  return (
    <div className="phone-input-wrapper" style={{ ...style }}>
      <PhoneInput
        id={id}
        international
        defaultCountry={country}
        value={value}
        onChange={handlePhoneNumberChange}
        required={required}
        disabled={disabled}
        preferredCountries={preferredCountries as Country[]}
        className={`form-control ${invalidClass}`}
        onCountryChange={(c) => setCountry(c as Country)}
        onBlur={handleBlur}
      />

      {showPlaceholder && exampleNumber && (
        <label className="floating-placeholder">{exampleNumber}</label>
      )}
    </div>
  );
};
